import React, { Fragment, useEffect, useState, useRef } from 'react';
import { Container } from '@material-ui/core';
import { useTranslation } from "react-i18next";
import ExitModalAndSave from '../Modal/ExitModalAndSave';
import { useDispatch, useSelector } from 'react-redux';
import { RouteComponentProps, useHistory } from "react-router-dom";
import * as api from "../../api";
import Button from "../../Button/Button";
import RepairJourney from '../RepairJourney';
import YellowAlert from '../YellowAlert';
import LoadingSpinner from "../../storybook/LoadingSpinner";
import { workOrderRepairObject } from '../../ducks/redux/actions/getAddress';
import { hasAdditionalNeeds, isSupportedResident } from '../../ducks/redux/actions/userChoices';
import { priorityCode, selectedRepairReasonPriorityOverride } from '../../ducks/redux/actions/dashboardReducer.actions';
import {
  addHazardAssessmentAnswer,
  saveHazardAssessment,
  saveHazardGetQuestionsData,
  saveHazardPostAssessmentResponse,
  saveHHSRSAssessmentCompleted,
  savePreAssessmentReturnQuestion,
  saveStandardRepairPriority,
  updateHazardAssessmentAnswer,
} from '../../ducks/redux/actions/hazardAssessment';
import { HazardAssessmentResponse, HazardQuestion, CreateHazardRequest, HazardAssessmentRecord, SavedHazardAssessment } from '../../models/ReferenceData/HazardAssessment';
import { RepairChannel } from '../../models/Repair/RepairChannel';
import moment from 'moment';
import { WorksOrderRequest } from '../../models/WorksOrders/WorksOrderRequest';
import { Helper } from "../../utils/Helper/HelperUtil";
import { getRepairPriority } from "../../utils/formatting";
import { RepairPriorityCode } from "../../models/Repair/CreateRepair";

interface HazardPreassessmentProps extends RouteComponentProps<any> { }

const dependencyResponsesMatch = (
  configuredResponses: string[],
  actualCodes: string[],
  _sourceQuestionType?: string
): boolean => configuredResponses.some((code) => actualCodes.includes(code));

export const isDependencyMatched = (
  question: HazardQuestion,
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[] = []
): boolean => {
  if (!question.dependsOn?.length) {
    return true;
  }

  return question.dependsOn.some((dependency) => {
    const actualCodes = answers[dependency.code] || [];
    const sourceQuestionType = sourceQuestions.find(
      (sourceQuestion) => sourceQuestion.code === dependency.code
    )?.type;

    return dependencyResponsesMatch(
      dependency.responses,
      actualCodes,
      sourceQuestionType
    );
  });
};

export const isDependencyMatchedForAnswer = (
  question: HazardQuestion,
  questionCode: string,
  answerCodes: string[],
  sourceQuestionType?: string
): boolean => question.dependsOn?.some((dependency) =>
  dependency.code === questionCode &&
  dependencyResponsesMatch(
    dependency.responses,
    answerCodes,
    sourceQuestionType
  )
) ?? false;


export const isPreAssessmentQuestion = (question: HazardQuestion): boolean =>
  Helper.isPreAssessmentCategory(question.category);

export const getQuestionAlertMessage = (question?: HazardQuestion): string => {
  const alert = question?.alerts?.[0];
  if (!alert) {
    return "";
  }

  if (typeof alert.message === "string") {
    return alert.message;
  }

  return alert.message?.colleague || alert.message?.default || "";
};

export const getQuestionAlertType = (question?: HazardQuestion): string =>
  question?.alerts?.[0]?.type || "";

const getPreviousQuestionIndex = (
  questions: HazardQuestion[],
  currentIndex: number,
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[]
) => {
  for (let index = currentIndex - 1; index >= 0; index--) {
    if (
      !questions[index].dependsOn?.length ||
      isDependencyMatched(questions[index], answers, sourceQuestions)
    ) {
      return index;
    }
  }

  return -1;
};

const getFirstApplicableQuestionIndex = (
  questions: HazardQuestion[],
  answers: { [key: string]: string[] },
  sourceQuestions: HazardQuestion[]
) => {
  const firstApplicableIndex = questions.findIndex(
    (question) =>
      !question.dependsOn?.length ||
      isDependencyMatched(question, answers, sourceQuestions)
  );

  return firstApplicableIndex === -1 ? 0 : firstApplicableIndex;
};

const normaliseRequest = (request: CreateHazardRequest) => ({
  assetType: request.assetType.toUpperCase(),
  hazardType: request.hazardType,
  contractorRating: request.contractorRating,
  hasAdditionalNeeds: request.hasAdditionalNeeds,
  isSupportedResident: request.isSupportedResident,
  responses: request.responses.map((response) => ({ code: response.code, answers: [...response.answers].sort((first, second) => first.localeCompare(second)) }))
    .sort((first, second) => first.code.localeCompare(second.code)),
});

const hasAssessmentChanged = (saved: SavedHazardAssessment | null | undefined, identifier: string | undefined,
  request: CreateHazardRequest): boolean => !saved || saved.identifier !== identifier ||
  JSON.stringify(normaliseRequest(saved.request)) !== JSON.stringify(normaliseRequest(request));

const restoreHazardAssessment = (assessment: HazardAssessmentRecord, workOrder: any, assetType: string): SavedHazardAssessment => {
  const conditions = assessment.outcome?.overridingConditions;
  return {
    identifier: assessment.id,
    request: {
      assetType, hazardType: assessment.hazardType,
      contractorRating: conditions?.contractorRating ?? null,
      hasAdditionalNeeds: conditions?.hasAdditionalNeeds ?? Boolean(workOrder?.assessmentInformation?.hasAdditionalNeeds),
      isSupportedResident: conditions?.isSupportedResident ?? Boolean(workOrder?.assessmentInformation?.isSupportedResident),
      responses: assessment.responses.map((response) => ({ ...response, answers: [...response.answers] })),
    },
    repairType: workOrder?.sorCode,
    isEmergency: Boolean(workOrder?.isEmergency),
    outcome: {
      id: assessment.id, hazardType: assessment.hazardType, score: assessment.outcome?.score ?? null,
      risk: assessment.outcome?.risk ?? null, actions: assessment.outcome?.actions ?? null
    },
  };
};

const HazardPreassessment: React.FC<HazardPreassessmentProps> = (props) => {
  const { t } = useTranslation();
  const history = useHistory();
  const dispatch = useDispatch();
  const selectedRepairReason = useSelector((state: any) => state?.dashboardReducer?.formState?.selectedRepairReasonObject);
  const assetDetails = useSelector((state: any) => state?.dashboardReducer?.formState?.assetByIdDataObject);
  const workOrderObject = useSelector((state: any) => state?.addresses?.workOrderRepair);
  const hazardResponses = useSelector((state: any) => state?.hazard?.responses || []);
  const cachedPreAssessmentJourneyQuestions = useSelector((state: any) => state?.hazard?.preAssessmentJourneyQuestions);
  const preAssessmentReturnQuestion = useSelector((state: any) => state?.hazard?.preAssessmentReturnQuestion || { code: "", from: "" });
  const standardRepairPriority = useSelector((state: any) => state?.hazard?.standardRepairPriority);
  const isAdditionalNeedsUser = useSelector((state: any) => state?.userChoicesReducer?.hasAdditionalNeeds);
  const supportedResident = useSelector((state: any) => state?.userChoicesReducer?.isSupportedResident);
  const isHHSRSRepairEnabledCheck = useSelector((state: any) => state?.userChoicesReducer?.isHHSRSRepairEnabled);
  const isHHSRSAssessmentCompleted = useSelector((state: any) => state?.hazard?.isHHSRSAssessmentCompleted === true);
  const userChosenSOR = useSelector((state: any) => state?.userChoicesReducer?.userChosenSOR);
  const customerAdditionalAlertData = useSelector((state: any) => state?.userChoicesReducer?.customerAdditionalAlertData);
  const customerDetails = useSelector((state: any) => state?.addresses?.saveCustomerDetails);
  const repairDiagnosticData = useSelector((state: any) => state?.userChoicesReducer?.repairDiagnosticFinalData);
  const [questions, setQuestions] = useState<HazardQuestion[]>([]);
  const [journeyQuestions, setJourneyQuestions] = useState<HazardQuestion[]>([]);
  const [answers, setAnswers] = useState<{ [key: string]: string[] }>({});
  const [currentIndex, setCurrentIndex] = useState(0);
  const [error, setError] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const savingRef = useRef(false);
  const savedAssessment = useSelector((state: any) => state?.hazard?.savedAssessment);
  const currentQuestion = questions[currentIndex];
  const selectedAnswers = currentQuestion ? answers[currentQuestion.code] || [] : [];
  const diagnosticId = selectedRepairReason?.[0]?.diagnosticId;
  const preAssessmentCategory = diagnosticId !== undefined && diagnosticId !== null
    ? `${Helper.PreAssessment.CategoryPrefix}${diagnosticId}`
    : "";
  const alertMessage = getQuestionAlertMessage(currentQuestion);
  const alertType = getQuestionAlertType(currentQuestion);
  const activeHazardCasePresent = useSelector((state: any) => state?.hazard?.hasActiveHazardCase);
  const activeHazardCase = useSelector((state: any) => state.hazard?.activeHazardCaseDetails);
  useEffect(() => {
    // The forward journey is gated on Repair Additional Details, so this page is only
    // reachable with the toggle off by direct URL entry. Redirect without a message.
    if (isHHSRSRepairEnabledCheck !== true) {
      props.history.replace("/LandingView");
      return;
    }

    if (selectedRepairReason?.[0]?.isPreAssessmentRequired !== true && selectedRepairReason?.[0]?.isHazard === true) {
      props.history.push("/HazardAssessment/1");
      return;
    }

    const loadPreAssessmentQuestions = async () => {
      try {
        setIsLoading(true);
        if (!preAssessmentCategory || !Array.isArray(cachedPreAssessmentJourneyQuestions)) {
          setQuestions([]);
          setJourneyQuestions([]);
          dispatch(saveHazardGetQuestionsData([]));
          return;
        }

        const allQuestions: HazardQuestion[] = cachedPreAssessmentJourneyQuestions;

        const normalisedQuestions = allQuestions.map((question) => ({
          ...question,
          options: [...question.options].sort((first, second) =>
            first.code.localeCompare(second.code, undefined, { numeric: true })
          ),
        }));
        const preAssessmentQuestions = normalisedQuestions
          .filter((question) =>
            Helper.isMatchingPreAssessmentCategory(
              question.category,
              preAssessmentCategory
            )
          )
          .sort((first, second) => first.sequence - second.sequence);
        const assessmentQuestions = normalisedQuestions
          .filter((question) => !isPreAssessmentQuestion(question))
          .sort((first, second) => first.sequence - second.sequence);

        setJourneyQuestions(normalisedQuestions);
        setQuestions(preAssessmentQuestions);
        dispatch(saveHazardGetQuestionsData(assessmentQuestions));

        const preAssessmentQuestionCodes = new Set(preAssessmentQuestions.map((question) => question.code));
        const restoredAnswers = hazardResponses.reduce((result: { [key: string]: string[] }, item: any) => {
          if (item.isPreAssessment && item.answerCodes?.length && preAssessmentQuestionCodes.has(item.code)) {
            result[item.code] = [...item.answerCodes];
          }
          return result;
        }, {});

        const existingAssessmentIdentifier =
          workOrderObject?.assessmentInformation?.identifier || workOrderObject?.assessmentIdentifier;

        if (existingAssessmentIdentifier && !Object.keys(restoredAnswers).length) {
          const savedAssessment = await api.getHazardAssessment(existingAssessmentIdentifier);
          dispatch(saveHazardAssessment(restoreHazardAssessment(savedAssessment, workOrderObject, assetDetails.assetType)));
          dispatch(hasAdditionalNeeds(savedAssessment.outcome?.overridingConditions?.hasAdditionalNeeds ?? Boolean(workOrderObject?.assessmentInformation?.hasAdditionalNeeds)));
          dispatch(isSupportedResident(savedAssessment.outcome?.overridingConditions?.isSupportedResident ?? Boolean(workOrderObject?.assessmentInformation?.isSupportedResident)));

          savedAssessment?.responses?.forEach((savedResponse) => {
            const question = normalisedQuestions.find((item) => item.code === savedResponse.code);
            if (!question || !savedResponse.answers?.length) {
              return;
            }

            const payload = {
              sequence: question.sequence,
              question: question.label,
              answers: question.options
                .filter((option) => savedResponse.answers.includes(option.code))
                .map((option) => option.text),
              answerCodes: [...savedResponse.answers],
              code: question.code,
              isPreAssessment: isPreAssessmentQuestion(question),
            };
            const existingQuestion = hazardResponses.some((item: any) => item.code === question.code);
            dispatch(existingQuestion ? updateHazardAssessmentAnswer(payload) : addHazardAssessmentAnswer(payload));

            if (payload.isPreAssessment) {
              restoredAnswers[question.code] = [...savedResponse.answers];
            }
          });
          if (savedAssessment?.outcome) {
            dispatch(saveHazardPostAssessmentResponse({
              id: savedAssessment.id,
              hazardType: savedAssessment.hazardType,
              ...savedAssessment.outcome,
            }));
          }
        }

        preAssessmentQuestions.forEach((question) => {
          if (question.dependsOn?.length && restoredAnswers[question.code]?.length && !isDependencyMatched(question, restoredAnswers, normalisedQuestions)) {
            const existingResponse = hazardResponses.find((item: any) => item.code === question.code);
            if (existingResponse) {
              dispatch(updateHazardAssessmentAnswer({ ...existingResponse, answers: [], answerCodes: [] }));
            }
            delete restoredAnswers[question.code];
          }
        });

        setAnswers(restoredAnswers);
        const canUseReturnQuestion = Boolean(preAssessmentReturnQuestion?.from);
        const returnQuestionIndex = canUseReturnQuestion
          ? preAssessmentQuestions.findIndex((question) =>
            question.code === preAssessmentReturnQuestion.code &&
            (!question.dependsOn?.length || isDependencyMatched(question, restoredAnswers, normalisedQuestions))
          )
          : -1;
        setCurrentIndex(returnQuestionIndex !== -1
          ? returnQuestionIndex
          : getFirstApplicableQuestionIndex(preAssessmentQuestions, restoredAnswers, normalisedQuestions));
        if (canUseReturnQuestion) {
          dispatch(savePreAssessmentReturnQuestion({ code: "", from: "" }));
        }
      } catch (e) {
        console.error(e);
        setQuestions([]);
        setJourneyQuestions([]);
        dispatch(saveHazardGetQuestionsData([]));
        props.history.push("/GenericError");
      } finally {
        setIsLoading(false);
      }
    };

    loadPreAssessmentQuestions();
    // Pre-assessment question data is cached in Redux for Back/re-entry in the same repair journey.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const saveResponse = (question: HazardQuestion, answerCodes: string[]) => {
    const payload = {
      sequence: question.sequence,
      question: question.label,
      answers: question.options
        .filter((response) => answerCodes.includes(response.code))
        .map((response) => response.text),
      answerCodes: [...answerCodes],
      code: question.code,
      isPreAssessment: true,
    };
    const existingQuestion = hazardResponses.some((item: any) => item.code === question.code);
    dispatch(existingQuestion ? updateHazardAssessmentAnswer(payload) : addHazardAssessmentAnswer(payload));
    return payload;
  };

  // On a resumed repair the Standard priority captured by Hazard Review is gone, because it
  // is held in session-scoped Redux. The Work Order API stores a single priority, which the
  // Hazard outcome overwrote, so the Standard code has to be determined again. Called only
  // when the cached value is missing, i.e. a resumed Hazard journey downgraded to Standard.
  const determineStandardPriorityCode = async (): Promise<string> => {
    const isEnhancedDiagnosticsRepair = workOrderObject?.isEnhancedDiagnosticsRepair;
    const priorityWorkProgramme = userChosenSOR !== ""
      ? (selectedRepairReason?.[0]?.sors?.find((sor: any) => sor?.sorCode === userChosenSOR)?.workProgramme
        ?? selectedRepairReason?.[0]?.sors?.[0]?.workProgramme)
      : selectedRepairReason?.[0]?.sors?.[0]?.workProgramme;

    // riskRating and assessmentType are deliberately omitted so the API returns the
    // Standard priority rather than the HHSRS one.
    const payload = {
      isEmergency: Boolean(workOrderObject?.isEmergency),
      repairType: userChosenSOR,
      description: workOrderObject?.repairReason,
      channel: RepairChannel.ColleagueSelfServe,
      raisedDate: moment(new Date()).format("YYYY-MM-DDTHH:mm:ss"),
      premisesType: assetDetails?.assetType,
      isAnaType: customerAdditionalAlertData?.length > 0,
      isV14Upgradable: isEnhancedDiagnosticsRepair
        ? repairDiagnosticData?.isV14Eligibile
        : selectedRepairReason?.[0]?.isV14Upgradable,
      tenureCode: customerDetails?.tenanciesSummaries?.[0]?.tenancySummary?.tenancyTenureCode ?? "",
      workProgramme: isEnhancedDiagnosticsRepair ? repairDiagnosticData?.workProgram : priorityWorkProgramme,
      hasSeasonalPriority: selectedRepairReason?.[0]?.hasSeasonalPriority ?? false
    };

    const response = await api.repairPriorityDetermination(payload as any);
    return response?.repairPriority || "";
  };

  const validateAssessmentIdentifier = (assessmentOutcome: HazardAssessmentResponse, existingAssessmentIdentifier?: string) => {
    if (!assessmentOutcome?.id) {
      throw new Error("Hazard assessment identifier was not returned");
    }

    if (existingAssessmentIdentifier && assessmentOutcome.id !== existingAssessmentIdentifier) throw new Error("Hazard assessment identifier changed");
  };

  const setupPriorityCodeInWorkOrder = async (workOrderObj: any) => {
    if (standardRepairPriority) {
      return;
    }

    if (!isHHSRSAssessmentCompleted) {
      return;
    }

    const determinedStandardPriorityCode = await determineStandardPriorityCode();
    if (!determinedStandardPriorityCode) {
      throw new Error("Standard repair priority was not returned");
    }

    workOrderObj.priority = determinedStandardPriorityCode;
    workOrderObj.priorityCode = determinedStandardPriorityCode;
    workOrderObj.isEmergency = determinedStandardPriorityCode === RepairPriorityCode.Emergency ||
      determinedStandardPriorityCode === RepairPriorityCode.OutOfHours;
  };

  const fetchOrCreateAssessmentOutcome = async (
    assessmentChanged: boolean,
    existingAssessmentIdentifier: string | undefined,
    request: CreateHazardRequest
  ): Promise<HazardAssessmentResponse> => {
    if (!assessmentChanged && savedAssessment) {
      return savedAssessment.outcome;
    }

    if (existingAssessmentIdentifier) {
      return await api.updateHazardAssessment(existingAssessmentIdentifier, request);
    }

    return (await api.createHazardAssessment(request))?.[0];
  };

  const dispatchPriorityUpdates = async (workOrderObj: any) => {
    if (standardRepairPriority) {
      dispatch(priorityCode(standardRepairPriority.priorityCode));
      dispatch(selectedRepairReasonPriorityOverride(standardRepairPriority.priorityOverride));
    } else if (isHHSRSAssessmentCompleted) {
      dispatch(priorityCode(workOrderObj.priority));
      dispatch(selectedRepairReasonPriorityOverride(await getRepairPriority(workOrderObj.priority)));
    }
  };

  const persistStandardOutcome = async (updatedResponses: any[]) => {
    const request = {
      assetType: assetDetails?.assetType?.toUpperCase() || "",
      hazardType: selectedRepairReason?.[0]?.hazardType || "",
      contractorRating: null,
      hasAdditionalNeeds: Boolean(isAdditionalNeedsUser),
      isSupportedResident: Boolean(supportedResident),
      responses: updatedResponses
        .filter((item: any) => item.isPreAssessment && item.answerCodes?.length)
        .map((item: any) => ({ code: item.code, answers: [...item.answerCodes] })),
    };

    const identifier = workOrderObject?.assessmentInformation?.identifier || workOrderObject?.assessmentIdentifier;
    const assessmentChanged = hasAssessmentChanged(savedAssessment, identifier, request);
  

    const standardWorkOrderObject = {
      ...workOrderObject,
      ...(standardRepairPriority && {
        priority: standardRepairPriority.workOrderPriority,
        priorityCode: standardRepairPriority.priorityCode,
        isEmergency: standardRepairPriority.isEmergency,
      }),
      isOutOfHours: false,
      hhsrsAssessment: undefined,
    };

    await setupPriorityCodeInWorkOrder(standardWorkOrderObject);

    const existingAssessmentIdentifier =
      standardWorkOrderObject?.assessmentInformation?.identifier || standardWorkOrderObject?.assessmentIdentifier;
    const assessmentOutcome = await fetchOrCreateAssessmentOutcome(assessmentChanged, existingAssessmentIdentifier, request);

    validateAssessmentIdentifier(assessmentOutcome, existingAssessmentIdentifier);
    workOrderObject.assessmentIdentifier = assessmentOutcome.id;
    dispatch(workOrderRepairObject({ ...workOrderObject }));

    const assessmentType = assessmentOutcome.hazardType || request.hazardType;
    if (!assessmentType) {
      throw new Error("Hazard assessment type was not returned");
    }

    standardWorkOrderObject.assessmentIdentifier = assessmentOutcome.id;
    standardWorkOrderObject.assessmentInformation = {
      type: assessmentType,
      identifier: assessmentOutcome.id,
      communicationPreference: workOrderObject?.assessmentInformation?.communicationPreference,
      hasAdditionalNeeds: Boolean(isAdditionalNeedsUser),
      isSupportedResident: Boolean(supportedResident),
    };

    standardWorkOrderObject.repairCauseId = Helper.GeneralWearandTear;
    standardWorkOrderObject.repairCause = Helper.RepairCauseLabels[Helper.GeneralWearandTear];

    const worksOrderRequest = new WorksOrderRequest(standardWorkOrderObject);
    await api.updateWorkOrderRepair(
      worksOrderRequest.updateWorkOrderParameter(standardWorkOrderObject),
      standardWorkOrderObject.id,
      "eTag"
    );

    await dispatchPriorityUpdates(standardWorkOrderObject);

    hazardResponses.filter((response: any) => !response.isPreAssessment && response.answerCodes?.length)
      .forEach((response: any) => {
        dispatch(updateHazardAssessmentAnswer({ ...response, answers: [], answerCodes: [] }));
      });

    dispatch(workOrderRepairObject(standardWorkOrderObject));
    dispatch(saveHazardPostAssessmentResponse(assessmentOutcome));
    dispatch(saveHazardAssessment({
      identifier: assessmentOutcome.id,
      request,
      outcome: assessmentOutcome,
      repairType: userChosenSOR,
      isEmergency: Boolean(standardWorkOrderObject.isEmergency)
    }));
    dispatch(saveHHSRSAssessmentCompleted(false));

    if (standardRepairPriority) {
      dispatch(saveStandardRepairPriority(null));
    }
  };

  const handleContinueButton = async () => {
    if (savingRef.current || isLoading || !currentQuestion) {
      return;
    }

    if (selectedAnswers.length === 0) {
      setError("Pre_Assessment_Select_Answer_Error");
      return;
    }

    setError("");
    const payload = saveResponse(currentQuestion, selectedAnswers);
    const currentAnswers = { ...answers, [currentQuestion.code]: [...selectedAnswers] };
    const updatedResponses = [
      ...questions
        .filter((question, index) => index <= currentIndex && currentAnswers[question.code]?.length)
        .map((question) => question.code === currentQuestion.code ? payload : ({
          sequence: question.sequence,
          question: question.label,
          answers: question.options
            .filter((response) => currentAnswers[question.code].includes(response.code))
            .map((response) => response.text),
          answerCodes: [...currentAnswers[question.code]],
          code: question.code,
          isPreAssessment: true,
        })),
    ];

    questions.slice(currentIndex + 1).forEach((question) => {
      if (question.dependsOn?.length && !isDependencyMatched(question, currentAnswers, journeyQuestions)) {
        const existingResponse = hazardResponses.find((item: any) => item.code === question.code);
        if (existingResponse?.answerCodes?.length) {
          dispatch(updateHazardAssessmentAnswer({ ...existingResponse, answers: [], answerCodes: [] }));
        }
        delete currentAnswers[question.code];
      }
    });

    const nextQuestionIndex = questions.findIndex((question, index) =>
      index > currentIndex &&
      isDependencyMatchedForAnswer(
        question,
        currentQuestion.code,
        selectedAnswers,
        currentQuestion.type
      )
    );

    const assessmentQuestions = journeyQuestions
      .filter((question) => !isPreAssessmentQuestion(question))
      .sort((first, second) => first.sequence - second.sequence);
    const matchingAssessmentQuestionIndex = assessmentQuestions.findIndex((question) =>
      isDependencyMatchedForAnswer(
        question,
        currentQuestion.code,
        selectedAnswers,
        currentQuestion.type
      )
    );

    const isMultiSelectQuestion = currentQuestion.type === "CHECKBOX";

    if (matchingAssessmentQuestionIndex !== -1 && (isMultiSelectQuestion || nextQuestionIndex === -1)) {
      const activePreAssessmentCodes = new Set(updatedResponses.map((response) => response.code));
      hazardResponses.filter((response: any) => response.isPreAssessment && !activePreAssessmentCodes.has(response.code))
        .forEach((response: any) => dispatch(updateHazardAssessmentAnswer({ ...response, answers: [], answerCodes: [] })));
     if (activeHazardCasePresent) {
        workOrderObject.caseIdentifier = activeHazardCase?.identifiers?.caseIdentifier;
        workOrderObject.hazardSorCode = selectedRepairReason?.[0]?.hazardSorCode;
        await persistStandardOutcome(updatedResponses);
        props.history.push("/RepairUploadmedia");
      }
      else {
        props.history.push(`/HazardAssessment/${matchingAssessmentQuestionIndex + 1}`);
      }
      return;
    }

    if (nextQuestionIndex !== -1) {
      setAnswers(currentAnswers);
      setCurrentIndex(nextQuestionIndex);
      return;
    }

    try {
      savingRef.current = true;
      setIsSaving(true);
      if (activeHazardCasePresent) {
        workOrderObject.caseIdentifier = '';
      }
      workOrderObject.hazardSorCode = "NA";
      await persistStandardOutcome(updatedResponses);
      props.history.push("/RepairUploadmedia");
    } catch (e) {
      console.error(e);
      props.history.push("/GenericError");
    } finally {
      savingRef.current = false;
      setIsSaving(false);
    }
  };

  const handleRadioChange = (code: string) => {
    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.code]: code ? [code] : [],
    }));
    setError("");
  };

  const handleCheckboxChange = (event: React.ChangeEvent<HTMLInputElement>, code: string) => {
    const currentAnswers = answers[currentQuestion.code] || [];
    const updatedAnswers = event.target.checked
      ? [...currentAnswers, code]
      : currentAnswers.filter((answerCode) => answerCode !== code);

    setAnswers((previousAnswers) => ({
      ...previousAnswers,
      [currentQuestion.code]: updatedAnswers,
    }));
    setError("");
  };

  const backButtonHandle = () => {
    setError("");
    const previousIndex = getPreviousQuestionIndex(questions, currentIndex, answers, journeyQuestions);
    if (previousIndex !== -1) {
      setCurrentIndex(previousIndex);
    } else {
      props.history.push("/RepairAdditionalDetails");
    }
  };

  const exitButtonHandle = () => history.push("/LandingView");
  const saveDraftAndExit = () => history.push(`/WorkOrder360View/${workOrderObject.id}`);
  const renderCurrentQuestion = () => {
    if (!currentQuestion) {
      return null;
    }

    const isMultiSelect = currentQuestion.type === "CHECKBOX";
    const isDropdown = currentQuestion.type === "DROPDOWN";
    const isRadio = currentQuestion.type === "RADIO";

    return (
      <div className="relative pb-2 bg-white" data-testid="hazard-preassessment">
        <h2 className="text-purple-100 text-xl sm:text-2xl font-semibold mb-4 sm:mb-6">
          {currentQuestion.label}
        </h2>

        {isDropdown && (
          <select
            disabled={isSaving}
            aria-label={currentQuestion.code}
            value={selectedAnswers[0] || ""}
            onChange={(event) => handleRadioChange(event.target.value)}
            className="block dropDownWidth rounded border border-grey mb-4 bg-grey"
          >
            <option value="">Select an option</option>
            {currentQuestion.options.map((option) => (
              <option key={option.code} value={option.code}>{option.text}</option>
            ))}
          </select>
        )}

        {isRadio && (
          <div className="mb-4">
            <div
              id={`${currentQuestion.code}-radiogroup`}
              role="radiogroup"
              aria-label={currentQuestion.label}
              className="grid grid-cols-1 gap-2 dropDownWidth"
            >
              {currentQuestion.options.map((option, index) => {
                const isChecked = selectedAnswers[0] === option.code;
                return (
                  <label
                    key={option.code}
                    id={`${currentQuestion.code}-label${index}`}
                    htmlFor={`${currentQuestion.code}-radio${index}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      padding: "10px 14px",
                      borderRadius: "8px",
                      border: isChecked ? "1px solid #0B8B84" : "1px solid #d1d5db",
                      backgroundColor: isChecked ? "#E6F6F5" : "#ffffff",
                      cursor: "pointer",
                      transition: "all 0.2s",
                      minHeight: "48px",
                    }}
                  >
                    <input
                      disabled={isSaving}
                      type="radio"
                      id={`${currentQuestion.code}-radio${index}`}
                      name={currentQuestion.code}
                      className="checkPos flex-shrink-0"
                      style={{ width: "18px", height: "18px", accentColor: "#0B8B84" }}
                      value={option.code}
                      checked={isChecked}
                      onChange={() => handleRadioChange(option.code)}
                      aria-label={option.text}
                    />
                    <span className="ml-3 text-sm w-90 inline-block">{option.text}</span>
                  </label>
                );
              })}
            </div>
          </div>
        )}

        {isMultiSelect && (
          <div className="grid grid-cols-1 gap-2 dropDownWidth">
            {currentQuestion.options.map((option) => {
              const isChecked = selectedAnswers.includes(option.code);
              return (
                <label
                  key={option.code}
                  htmlFor={`${currentQuestion.code}-${option.code}`}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    padding: "10px 14px",
                    borderRadius: "8px",
                    border: isChecked ? "1px solid #0B8B84" : "1px solid #d1d5db",
                    backgroundColor: isChecked ? "#E6F6F5" : "#ffffff",
                    cursor: "pointer",
                    transition: "all 0.2s",
                    minHeight: "48px",
                  }}
                >
                  <input
                    disabled={isSaving}
                    type="checkbox"
                    id={`${currentQuestion.code}-${option.code}`}
                    name={currentQuestion.code}
                    className="checkPos flex-shrink-0"
                    style={{ width: "18px", height: "18px", accentColor: "#0B8B84" }}
                    value={option.code}
                    checked={isChecked}
                    onChange={(event) => handleCheckboxChange(event, option.code)}
                    aria-label={option.text}
                  />
                  <span className="ml-3 text-sm w-90 inline-block">{option.text}</span>
                </label>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <Container id="reploc-container" role="main" className="ctn-fht px-4">
      {isLoading || isSaving ? (
        <div className="div-loader marign-left-smallscreen-0">
          <div className="SpinnerWrapper" id="SpinnerWrapper">
            <LoadingSpinner />
          </div>
        </div>
      ) : (
        <Fragment>
          <div className="mx-auto mb-0">
            <div className="back-and-journey">
              <p className="text-teal text-xl mt-4 mb-4 md:mb-12">
                <button type={undefined} className="back-btn" data-testid="back-button_hazard-pre" onClick={backButtonHandle} disabled={isSaving}>
                  <i className="fas fa-angle-left mr-2"></i>
                  {t("back")}
                </button>
              </p>
              <div className=""></div>
              {<RepairJourney />}
            </div>
          </div>

          {currentQuestion && alertMessage && Helper.isInformationAlertType(alertType) && (
            <div data-testid="preassessment-alert" className="error mb-8 mt-2" role="alert" >
              <div className="w-full bg-purple-20 flex items-center py-2 px-2">
                <div className="px-4 pr-8">
                  <i className="fa fa-bullhorn text-purple-100 w-6 h-6 purple-icon"></i>
                </div>
                <div className="flex items-center text-black">
                  <p className="w-full text-left my-2 font-AvantGardeGothic-Md">
                    {alertMessage}
                  </p>
                </div>
              </div>
            </div>
          )}

          {renderCurrentQuestion()}
          {error !== "" && <YellowAlert messageText={error} {...props} />}

          <div className="flex justify-between items-center pb-4 md:pb-8" id="pre-assesement-main-btn">
            <ExitModalAndSave id="assesement-exitmodal" exit={exitButtonHandle} saveDraftAndExit={saveDraftAndExit} disabled={isLoading || isSaving || !currentQuestion} />
            <Button
              id="btn_hhrs_continue"
              onClick={handleContinueButton}
              className="button-class"
              disabled={isLoading || isSaving || !currentQuestion}
              aria-busy={isSaving}
            >
              {t("continue")}
              <i className="fas fa-angle-right relativetext-white pointer-events-none arrow"></i>
            </Button>
          </div>
        </Fragment>
      )}
    </Container>
  );
};

export { HazardPreassessment };
